import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

import {
  closeExperimentDrawer,
  closeExperimentModal,
  openExperimentDrawer,
  openExperimentModal,
  setExperimentSource,
} from 'modules/experiments/takeover/ducks/actions';
import {
  getCampaignInfoState,
  getExperimentTakeoverMetaData,
  getExperimentTakeoverStatus,
} from 'modules/experiments/takeover/ducks/selectors';

import {
  CampaignInfoState,
  ExperimentTakeoverMetaData,
  GetExperimentLinkAttributes,
} from '../types';
import {
  addExperimentIdUrlParam,
  removeExperimentIdUrlParam,
  routeToExperimentModule,
} from '../utils';

export const useExperimentTakeover = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { pathname, search } = location;

  const { isModalOpen: isExperimentCreationModalOpen, isDrawerOpen: isExperimentTakeoverOpen } =
    useSelector(getExperimentTakeoverStatus);

  const openExperimentTakeoverViaUrlParam = (experimentId: number) => {
    const newUrl = addExperimentIdUrlParam({
      experimentId,
      urlParams: search,
      currentUrl: pathname,
    });

    history.push(newUrl);
  };

  const openExperimentCreationModal = (campaignInfo: CampaignInfoState) =>
    dispatch(openExperimentModal(campaignInfo));
  const openExperimentTakeover = (experimentId?: number) =>
    experimentId ? dispatch(openExperimentDrawer(experimentId)) : null; // The `experimentId` could potentially be 0, which is an invalid/placeholder id
  const getExperimentLinkAttributes = (args: GetExperimentLinkAttributes) =>
    routeToExperimentModule(args, openExperimentTakeoverViaUrlParam);

  return {
    isExperimentCreationModalOpen,
    isExperimentTakeoverOpen,
    getExperimentLinkAttributes,
    openExperimentCreationModal,
    openExperimentTakeover,
  };
};

/**
 * To be used inside the experiments/takeover module only.
 */
export const usePrivateExperimentTakeover = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { pathname, search } = location;

  const experimentTakeoverMetaData = useSelector(getExperimentTakeoverMetaData);
  const campaignInfo = useSelector(getCampaignInfoState);

  const setExperimentTakeoverSource = (source: ExperimentTakeoverMetaData['source']) =>
    dispatch(setExperimentSource(source));
  const openExperimentTakeoverAfterCreation = (experimentId: number) => {
    const newUrl = addExperimentIdUrlParam({
      experimentId,
      urlParams: search,
      currentUrl: pathname,
    });

    history.push(newUrl);
  };
  const closeExperimentCreationModal = () => dispatch(closeExperimentModal());
  const closeExperimentTakeover = () => {
    const newUrl = removeExperimentIdUrlParam({
      urlParams: search,
      currentUrl: pathname,
    });

    history.push(newUrl);

    dispatch(closeExperimentDrawer());
  };

  return {
    ...useExperimentTakeover(),
    experimentTakeoverMetaData,
    campaignInfo,
    setExperimentTakeoverSource,
    openExperimentTakeoverAfterCreation,
    closeExperimentCreationModal,
    closeExperimentTakeover,
  };
};
